/**
 * Admin impersonation: swap the session to a short-lived token for the
 * target user, stashing the admin's own tokens for the return trip. Full
 * page reloads on both directions keep every cache identity-clean.
 */

import { api } from '@/services/api';
import {
  clearImpersonationStash,
  readImpersonationStash,
  saveImpersonationStash,
} from '@/services/impersonation-stash';

interface ImpersonationStartResponse {
  accessToken: string;
  expiresIn: number;
  user: { id: string; email: string; role: string; permissions: string[] };
}

const TOKEN_KEY = 'nord-living-token';
const REFRESH_KEY = 'nord-living-refresh';
const TOKEN_EXPIRY_KEY = 'nord-living-token-expiry';

export async function startImpersonation(userId: string): Promise<void> {
  const response = await api.post(`/auth/impersonation/${userId}`);
  const data = (response.data.data ?? response.data) as ImpersonationStartResponse;

  saveImpersonationStash({
    accessToken: localStorage.getItem(TOKEN_KEY) ?? '',
    refreshToken: localStorage.getItem(REFRESH_KEY) ?? '',
    tokenExpiry: localStorage.getItem(TOKEN_EXPIRY_KEY) ?? '',
    targetEmail: data.user.email,
  });
  localStorage.setItem(TOKEN_KEY, data.accessToken);
  localStorage.setItem(REFRESH_KEY, '');
  localStorage.setItem(
    TOKEN_EXPIRY_KEY,
    String(Date.now() + data.expiresIn * 1000),
  );
  window.location.assign('/');
}

export async function endImpersonation(): Promise<void> {
  const stash = readImpersonationStash();
  if (!stash) return;
  try {
    // Best-effort bookkeeping while the impersonated token is still active;
    // the backend derives actor/target from the token's own claims.
    await api.post('/auth/impersonation/end');
  } catch {
    // The audit trail still has the start event; never block the return.
  }
  clearImpersonationStash();
  localStorage.setItem(TOKEN_KEY, stash.accessToken);
  localStorage.setItem(REFRESH_KEY, stash.refreshToken);
  localStorage.setItem(TOKEN_EXPIRY_KEY, stash.tokenExpiry);
  window.location.assign('/admin/users');
}

export function getImpersonationTargetEmail(): string | null {
  return readImpersonationStash()?.targetEmail ?? null;
}
